if (!customElements.get('watch-strap-configurator-v2')) {
	class WatchStrapConfiguratorV2 extends HTMLElement {
		constructor() {
			super()

			this.swiperContainer = this.querySelector('.strap-swiper')
			this.styleTabs = this.querySelectorAll('.watch-style-tab')
			this.faceTabs = this.querySelectorAll('.watch-face-tab')
			this.strapSlides = this.querySelectorAll('.strap-slide')
			this.paginationEl = this.querySelector('.strap-pagination')

			this.styleTablist = this.querySelector('.watch-styles-tabs')
			this.faceTablist = this.querySelector('.watch-faces-tabs')

			this.watchOnlyForm = this.querySelector('.watch-only-form')
			this.strapOnlyForm = this.querySelector('.strap-only-form')
			this.bundleForm = this.querySelector('.bundle-form')
			this.notifyLink = this.querySelector('.strap-notify-link')

			this.locale = this.dataset.locale || 'en-GB'
			this.currency = this.dataset.currency || 'GBP'
			this.mode = this.dataset.mode === 'watches' ? 'watches' : 'straps'
			this.fixedStrapDataEl = this.querySelector('.fixed-strap-data')

			if (this.bundleForm) {
				const bundleBtn = this.bundleForm.querySelector('button')
				if (bundleBtn && !bundleBtn.dataset.baseText) {
					bundleBtn.dataset.baseText = bundleBtn.textContent.trim()
				}
			}

			this.numberOfSlides = this.querySelectorAll('.swiper-slide').length
			this.initialSlide = this.numberOfSlides > 5 ? 2 : 0

			this.state = {
				currentStyle: '',
				currentFace: '',
				currentWatchId: '',
				currentWatchVariantId: '',
				currentWatchPriceCents: 0,
				currentWatchAvailable: true,
				currentWatchDiscontinued: false,
				currentWatchUrl: '',
				currentWatchName: '',
				currentStrapId: '',
				currentStrapVariantId: '',
				currentStrapPriceCents: 0,
				currentStrapAvailable: true,
				currentStrapUrl: '',
				currentStrapName: ''
			}

			if (this.mode === 'watches' && this.fixedStrapDataEl) {
				const el = this.fixedStrapDataEl
				this.state.currentStrapId = el.dataset.productId || ''
				this.state.currentStrapVariantId = el.dataset.variantId || ''
				this.state.currentStrapPriceCents = parseInt(el.dataset.priceCents, 10) || 0
				this.state.currentStrapAvailable = el.dataset.available !== 'false'
				this.state.currentStrapUrl = el.dataset.productUrl || ''
				this.state.currentStrapName = el.dataset.productTitle || ''
			}

			this.swiper = null
			this.swiperInitialized = false

			this.visibleSlidesMap = new Map()

			this.pendingTimeouts = new Set()
			this.pendingRAFs = new Set()

			this._onResize = this.handleResize.bind(this)
			this._onSectionLoad = this.init.bind(this)
			this._onDOMReady = this.init.bind(this)
			this._onStyleTablistKey = this.handleTablistKeydown.bind(this, 'style')
			this._onFaceTablistKey = this.handleTablistKeydown.bind(this, 'face')

			this.swiperOptions = {
				a11y: false,
				initialSlide: this.initialSlide,
				threshold: 3,
				spaceBetween: 9,
				slidesPerView: 5,
				slidesPerGroup: 1,
				resistanceRatio: 0.72,
				centeredSlides: true,
				slideToClickedSlide: true,
				loop: false,
				navigation: {
					enabled: true,
					nextEl: '.swiper-button-next',
					prevEl: '.swiper-button-prev',
				},
				pagination: false,
				breakpoints: {
					320: {
						navigation: { enabled: false },
						slidesPerView: 1.7,
						slidesPerGroup: 1,
						initialSlide: 0,
						spaceBetween: 0
					},
					768: {
						enabled: true,
						slidesPerView: 3,
						slidesPerGroup: 1,
						initialSlide: this.initialSlide
					},
					1200: {
						enabled: true,
						slidesPerView: 5,
						slidesPerGroup: 1,
						initialSlide: this.initialSlide
					}
				},
				on: {
					slideChange: () => {
						this.hydrateVisibleSlides()
						this.updateActiveStrapInfo()
						this.updateCustomPagination()
					},
					init: () => {
						this.hydrateVisibleSlides()
						this.scheduleBackgroundHydration()
						this.setManagedTimeout(() => {
							if (!this.swiper) return
							this.updateVisibleSlidesMap()
							this.updateActiveStrapInfo()
							this.renderCustomPagination()
							this.updateCustomPagination()
						}, 100)
					}
				}
			}
		}

		setManagedTimeout(fn, ms) {
			const id = setTimeout(() => {
				this.pendingTimeouts.delete(id)
				fn()
			}, ms)
			this.pendingTimeouts.add(id)
			return id
		}

		setManagedRAF(fn) {
			const id = requestAnimationFrame(() => {
				this.pendingRAFs.delete(id)
				fn()
			})
			this.pendingRAFs.add(id)
			return id
		}

		cancelManagedAsync() {
			this.pendingTimeouts.forEach(id => clearTimeout(id))
			this.pendingTimeouts.clear()
			this.pendingRAFs.forEach(id => cancelAnimationFrame(id))
			this.pendingRAFs.clear()
		}

		handleResize() {
			if (!this.swiperInitialized) return
			if (this._resizeTimeoutId) {
				clearTimeout(this._resizeTimeoutId)
				this.pendingTimeouts.delete(this._resizeTimeoutId)
			}
			this._resizeTimeoutId = this.setManagedTimeout(() => {
				this._resizeTimeoutId = null
				if (!this.swiper || !this.swiperInitialized) return
				this.updateSwiperOptions()
				this.swiper.update()
				this.updateVisibleSlidesMap()
				this.updateActiveStrapInfo()
				this.renderCustomPagination()
				this.updateCustomPagination()
			}, 150)
		}

		formatMoney(cents, currency) {
			const amount = (parseInt(cents, 10) || 0) / 100
			const code = currency || this.currency
			try {
				return new Intl.NumberFormat(this.locale, {
					style: 'currency',
					currency: code,
					minimumFractionDigits: 0,
					maximumFractionDigits: 2
				}).format(amount)
			} catch (e) {
				return `${code} ${amount.toFixed(2)}`
			}
		}

		calculateInitialSlide(visibleSlidesCount) {
			return visibleSlidesCount > 5 ? 2 : 0
		}

		hydrateSlide(slide) {
			if (!slide || slide.dataset.hydrated === 'true') return
			const templates = slide.querySelectorAll('template.lazy-img-tpl')
			if (!templates.length) {
				slide.dataset.hydrated = 'true'
				return
			}
			templates.forEach(tpl => {
				const parent = tpl.parentNode
				if (!parent) return
				while (tpl.content.firstChild) {
					const node = tpl.content.firstChild
					parent.appendChild(node)
					if (node.nodeType === 1) {
						const imgs = node.tagName === 'IMG' ? [node] : node.querySelectorAll ? node.querySelectorAll('img') : []
						imgs.forEach(img => {
							img.loading = 'eager'
							img.decoding = 'async'
						})
					}
				}
				tpl.remove()
			})
			slide.dataset.hydrated = 'true'
		}

		getHydratableSlides() {
			if (!this.swiperContainer) return []
			return Array.from(this.swiperContainer.querySelectorAll('.swiper-slide')).filter(slide => slide.style.display !== 'none')
		}

		hydrateVisibleSlides() {
			if (!this.swiper) return
			const slides = this.getHydratableSlides()
			if (!slides.length) return
			const activeSlide = this.swiperContainer.querySelector('.swiper-slide-active')
			let active = activeSlide ? slides.indexOf(activeSlide) : -1
			if (active < 0) active = Math.min(this.swiper.activeIndex || 0, slides.length - 1)
			if (active < 0) active = 0
			const view = Math.ceil(this.swiper.params.slidesPerView) || 1
			const start = Math.max(0, active - 1)
			const end = Math.min(slides.length - 1, active + view + 1)
			for (let i = start; i <= end; i++) {
				this.hydrateSlide(slides[i])
			}
		}

		scheduleBackgroundHydration() {
			if (!this.swiper || this._bgHydrationScheduled) return
			this._bgHydrationScheduled = true
			const token = (this._bgHydrationToken || 0) + 1
			this._bgHydrationToken = token
			const start = () => {
				const step = () => {
					if (token !== this._bgHydrationToken) return
					const remaining = this.getHydratableSlides().filter(s => s.dataset.hydrated !== 'true')
					if (!remaining.length) {
						this._bgHydrationScheduled = false
						return
					}
					this.hydrateSlide(remaining.shift())
					this.setManagedTimeout(step, 180)
				}
				step()
			}
			if ('IntersectionObserver' in window) {
				const io = new IntersectionObserver((entries, observer) => {
					if (entries.some(e => e.isIntersecting)) {
						observer.disconnect()
						start()
					}
				}, { rootMargin: '300px' })
				io.observe(this)
				this._hydrationObserver = io
			} else {
				start()
			}
		}

		resetBackgroundHydration() {
			this._bgHydrationScheduled = false
			this._bgHydrationToken = (this._bgHydrationToken || 0) + 1
			if (this._hydrationObserver) {
				this._hydrationObserver.disconnect()
				this._hydrationObserver = null
			}
		}

		updateSwiperOptions() {
			const visibleSlidesCount = this.getVisibleStraps().length
			this.initialSlide = this.calculateInitialSlide(visibleSlidesCount)
			this.swiperOptions.initialSlide = this.initialSlide
			this.swiperOptions.breakpoints[1200].initialSlide = this.initialSlide

			if (this.swiper && this.swiperInitialized) {
				this.swiper.params.initialSlide = this.initialSlide
				this.swiper.update()
			}
		}

		connectedCallback() {
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', this._onDOMReady, { once: true })
			} else {
				this.init()
			}
			if (typeof Shopify !== 'undefined' && Shopify.designMode) {
				document.addEventListener('shopify:section:load', this._onSectionLoad)
			}
		}

		init() {
			this.setupEventListeners()
			this.initializeFirstStyle()
		}

		clearPaginationEl() {
			if (!this.paginationEl) return
			while (this.paginationEl.firstChild) {
				this.paginationEl.removeChild(this.paginationEl.firstChild)
			}
		}

		renderCustomPagination() {
			if (!this.paginationEl || !this.swiper) return

			this.clearPaginationEl()

			const snapGrid = this.swiper.snapGrid || []
			const pageCount = snapGrid.length

			if (pageCount <= 1) {
				this.paginationEl.classList.remove('is-active')
				return
			}

			for (let i = 0; i < pageCount; i++) {
				const bullet = document.createElement('button')
				bullet.type = 'button'
				bullet.className = 'strap-pagination__bullet'
				bullet.dataset.page = String(i)
				bullet.setAttribute('aria-label', `Go to page ${i + 1}`)
				bullet.addEventListener('click', () => {
					const page = parseInt(bullet.dataset.page, 10)
					if (this.swiper && this.swiper.snapGrid && this.swiper.snapGrid[page] !== undefined) {
						this.swiper.slideTo(page)
					}
				})
				this.paginationEl.appendChild(bullet)
			}
			this.paginationEl.classList.add('is-active')
		}

		updateCustomPagination() {
			if (!this.paginationEl || !this.swiper) return
			const activeIdx = this.swiper.activeIndex
			this.paginationEl.querySelectorAll('.strap-pagination__bullet').forEach((bullet, i) => {
				bullet.classList.toggle('is-active', i === activeIdx)
			})
		}

		setupEventListeners() {
			this.styleTabs.forEach(tab => {
				tab.addEventListener('click', event => this.handleStyleChange(event))
			})

			this.faceTabs.forEach(tab => {
				tab.addEventListener('click', event => this.handleFaceChange(event))
			})

			if (this.styleTablist) {
				this.styleTablist.addEventListener('keydown', this._onStyleTablistKey)
			}
			if (this.faceTablist) {
				this.faceTablist.addEventListener('keydown', this._onFaceTablistKey)
			}

			window.addEventListener('resize', this._onResize)

			this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification')
			;[this.watchOnlyForm, this.strapOnlyForm, this.bundleForm].forEach(form => {
				if (!form) return
				form.addEventListener('submit', this.onCartFormSubmit.bind(this))
			})

			if (typeof Shopify !== 'undefined' && Shopify.designMode) {
				this.parent = this.closest('section')
				this.observer = new MutationObserver(() => {
					if (this.swiper) {
						this.updateSwiperOptions()
						this.swiper.update()
						this.setManagedTimeout(() => {
							if (!this.swiper || !this.swiperInitialized) return
							this.updateVisibleSlidesMap()
							this.updateActiveStrapInfo()
							this.renderCustomPagination()
							this.updateCustomPagination()
						}, 100)
					}
				})
				this.observer.observe(this.parent, { childList: true, subtree: true })
			}
		}

		handleTablistKeydown(kind, event) {
			const tabs = kind === 'style'
				? Array.from(this.styleTabs).filter(t => t.style.display !== 'none')
				: Array.from(this.faceTabs).filter(t => t.style.display !== 'none')

			if (tabs.length === 0) return

			const current = document.activeElement
			const currentIdx = tabs.indexOf(current)
			let nextIdx = -1

			switch (event.key) {
				case 'ArrowRight':
				case 'ArrowDown':
					nextIdx = currentIdx >= 0 ? (currentIdx + 1) % tabs.length : 0
					break
				case 'ArrowLeft':
				case 'ArrowUp':
					nextIdx = currentIdx > 0 ? currentIdx - 1 : tabs.length - 1
					break
				case 'Home':
					nextIdx = 0
					break
				case 'End':
					nextIdx = tabs.length - 1
					break
				case 'Enter':
				case ' ':
					if (current && tabs.includes(current)) {
						event.preventDefault()
						current.click()
					}
					return
				default:
					return
			}

			if (nextIdx >= 0 && tabs[nextIdx]) {
				event.preventDefault()
				tabs[nextIdx].focus()
			}
		}

		updateTabAria(activeTab, tablist) {
			if (!tablist) return
			const tabs = tablist.querySelectorAll('[role="tab"]')
			tabs.forEach(tab => {
				const isActive = tab === activeTab
				tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
				tab.setAttribute('tabindex', isActive ? '0' : '-1')
			})
		}

		initializeFirstStyle() {
			if (this.styleTabs.length > 0) {
				this.styleTabs[0].click()
				return
			}
			if (this.mode === 'straps') {
				const firstFaceTab = this.querySelector('.watch-face-tab')
				if (firstFaceTab) {
					firstFaceTab.classList.add('active')
					this.updateTabAria(firstFaceTab, this.faceTablist)
					this.handleFaceSelection(firstFaceTab)
				}
			}
			this.updateVisibleStraps()
		}

		handleStyleChange(event) {
			const style = event.currentTarget.dataset.style
			this.state.currentStyle = style

			this.styleTabs.forEach(tab => tab.classList.remove('active'))
			event.currentTarget.classList.add('active')
			this.updateTabAria(event.currentTarget, this.styleTablist)

			this.destroySwiper()

			if (this.mode === 'straps') {
				let firstVisibleFaceTab = null
				let hasActiveFaceTab = false

				this.faceTabs.forEach(faceTab => {
					if (faceTab.dataset.style === style) {
						faceTab.style.display = 'block'
						if (!firstVisibleFaceTab) firstVisibleFaceTab = faceTab
						if (faceTab.classList.contains('active')) hasActiveFaceTab = true
					} else {
						faceTab.style.display = 'none'
					}
				})

				if (!hasActiveFaceTab && firstVisibleFaceTab) {
					firstVisibleFaceTab.classList.add('active')
					this.updateTabAria(firstVisibleFaceTab, this.faceTablist)
					this.handleFaceSelection(firstVisibleFaceTab)
				} else {
					const currentActiveFaceTab = this.querySelector('.watch-face-tab.active')
					if (currentActiveFaceTab && currentActiveFaceTab.dataset.style === style) {
						this.updateTabAria(currentActiveFaceTab, this.faceTablist)
						this.handleFaceSelection(currentActiveFaceTab)
					} else if (firstVisibleFaceTab) {
						firstVisibleFaceTab.classList.add('active')
						this.updateTabAria(firstVisibleFaceTab, this.faceTablist)
						this.handleFaceSelection(firstVisibleFaceTab)
					}
				}
			}

			this.updateSwiperOptions()
			this.updateVisibleStraps()
		}

		handleFaceChange(event) {
			this.destroySwiper()

			this.faceTabs.forEach(tab => tab.classList.remove('active'))
			event.currentTarget.classList.add('active')
			this.updateTabAria(event.currentTarget, this.faceTablist)
			this.handleFaceSelection(event.currentTarget)

			this.setManagedTimeout(() => {
				this.updateSwiperOptions()
				this.updateVisibleStraps()
			}, 50)
		}

		destroySwiper() {
			this.cancelManagedAsync()
			this._resizeTimeoutId = null
			this.resetBackgroundHydration()
			if (this.swiper) {
				try { this.swiper.destroy(true, true) } catch (e) {}
				this.swiper = null
				this.swiperInitialized = false
				this.visibleSlidesMap.clear()
			}
			this.clearPaginationEl()
			if (this.paginationEl) {
				this.paginationEl.classList.remove('is-active')
			}
		}

		handleFaceSelection(faceTab) {
			this.state.currentFace = faceTab.dataset.face
			this.state.currentWatchId = faceTab.dataset.watchId
			this.state.currentWatchVariantId = faceTab.dataset.watchVariant || ''
			this.state.currentWatchPriceCents = parseInt(faceTab.dataset.watchPriceCents, 10) || 0
			this.state.currentWatchAvailable = faceTab.dataset.watchAvailable !== 'false'
			this.state.currentWatchDiscontinued = faceTab.dataset.watchDiscontinued === 'true'
			this.activateWatchFaces(this.state.currentFace)
			this.updatePurchaseForms()
			this.applyStrapOrder(faceTab)
		}

		activateWatchFaces(face) {
			this.querySelectorAll('.watch-face').forEach(watchFace => {
				watchFace.classList.toggle('active', watchFace.dataset.face === face)
			})
		}

		applyStrapOrder(activeFaceTab) {
			if (this.mode !== 'straps') return
			const orderAttr = activeFaceTab.dataset.strapOrder || ''
			const orderedIds = orderAttr ? orderAttr.split(',').filter(Boolean) : []
			const slides = Array.from(this.querySelectorAll('.strap-slide'))
			if (slides.length === 0) return
			const wrapper = slides[0].parentElement
			if (!wrapper) return
			slides.forEach((slide, i) => {
				slide.style.order = ''
				if (slide.dataset.originalIdx === undefined) slide.dataset.originalIdx = String(i)
			})
			const defaultRank = slide => Number(slide.dataset.originalIdx)
			let sorted
			if (orderedIds.length > 0) {
				const rank = slide => {
					const i = orderedIds.indexOf(slide.dataset.productId)
					return i === -1 ? Number.MAX_SAFE_INTEGER : i
				}
				sorted = slides.slice().sort((a, b) => {
					const ra = rank(a), rb = rank(b)
					return ra === rb ? defaultRank(a) - defaultRank(b) : ra - rb
				})
			} else {
				sorted = slides.slice().sort((a, b) => defaultRank(a) - defaultRank(b))
			}
			sorted.forEach(slide => wrapper.appendChild(slide))
			this.initialSlide = 0
			if (this.swiper) {
				this.swiper.update()
				this.swiper.slideTo(0, 0)
				this.hydrateVisibleSlides()
				this.setManagedTimeout(() => {
					if (!this.swiper) return
					this.swiper.slideTo(0, 0)
					this.hydrateVisibleSlides()
				}, 200)
			}
		}

		updateVisibleStraps() {
			this.getVisibleStraps()

			this.setManagedRAF(() => {
				this.setManagedTimeout(() => {
					this.initSwiper()
				}, 100)
			})
		}

		getVisibleStraps() {
			const visibleStraps = []
			this.visibleSlidesMap.clear()

			this.strapSlides.forEach(strap => {
				if (strap.dataset.style === this.state.currentStyle) {
					strap.style.display = 'block'
					visibleStraps.push(strap)
				} else {
					strap.style.display = 'none'
				}
			})

			return visibleStraps
		}

		updateVisibleSlidesMap() {
			if (!this.swiper) return

			this.visibleSlidesMap.clear()
			const visibleElements = this.querySelectorAll('.strap-slide[style*="block"]')

			visibleElements.forEach((element, swiperIndex) => {
				this.visibleSlidesMap.set(swiperIndex, element)
			})
		}

		initSwiper() {
			if (this.swiperInitialized) {
				this.swiper.destroy(true, true)
				this.swiperInitialized = false
			}

			const visibleSlides = this.getVisibleStraps()
			if (visibleSlides.length === 0) return

			this.swiper = new Swiper(this.swiperContainer, this.swiperOptions)

			this.swiper.wrapperEl.setAttribute('aria-live', 'polite')
			this.swiper.wrapperEl.id = `watch-swiper-${this.dataset.sectionId || Date.now()}`

			this.hydrateVisibleSlides()
			this.scheduleBackgroundHydration()

			this.setManagedTimeout(() => {
				if (!this.swiper) return
				this.swiper.slides.forEach((slide, index) => {
					slide.setAttribute('role', 'group')
					slide.setAttribute('aria-label', `${index + 1} / ${this.swiper.slides.length}`)
					slide.dataset.index = index
				})

				this.updateVisibleSlidesMap()

				const navButtons = [
					this.swiper.navigation.prevEl,
					this.swiper.navigation.nextEl
				]

				navButtons.forEach((button, index) => {
					if (button) {
						const type = index === 0 ? 'Previous' : 'Next'
						button.setAttribute('aria-label', `${type} strap`)
						button.setAttribute('aria-controls', this.swiper.wrapperEl.id)
					}
				})

				this.swiperInitialized = true

				this.setManagedTimeout(() => {
					if (!this.swiper || !this.swiperInitialized) return
					this.updateActiveStrapInfo()
					this.renderCustomPagination()
					this.updateCustomPagination()
				}, 150)
			}, 50)
		}

		updateActiveStrapInfo() {
			if (!this.swiper || typeof this.swiper.realIndex === 'undefined') {
				const firstVisibleSlide = this.querySelector('.strap-slide[style*="block"]')
				if (firstVisibleSlide) {
					this.updateStrapInfo(firstVisibleSlide)
				}
				return
			}

			const swiperIndex = this.swiper.realIndex
			const activeSlideElement = this.visibleSlidesMap.get(swiperIndex)

			if (activeSlideElement) {
				this.updateStrapInfo(activeSlideElement)
			} else {
				const swiperSlide = this.swiper.slides[swiperIndex]
				if (swiperSlide) {
					const strapSlide = swiperSlide.classList.contains('strap-slide')
						? swiperSlide
						: swiperSlide.querySelector('.strap-slide')
					if (strapSlide && strapSlide.style.display !== 'none') {
						this.updateStrapInfo(strapSlide)
					}
				}
			}
		}

		updateStrapInfo(slideElement) {
			const strapInfo = this.querySelector('.strap-info')
			if (!strapInfo || !slideElement) return

			let activeName, activePriceCents, activeAvailable, activeUrl

			if (this.mode === 'watches') {
				activeName = slideElement.dataset.watchTitle || ''
				activePriceCents = parseInt(slideElement.dataset.watchPriceCents, 10) || 0
				activeAvailable = slideElement.dataset.watchAvailable !== 'false'
				activeUrl = slideElement.dataset.watchUrl || ''

				this.state.currentWatchId = slideElement.dataset.watchId || ''
				this.state.currentWatchVariantId = slideElement.dataset.watchVariant || ''
				this.state.currentWatchPriceCents = activePriceCents
				this.state.currentWatchAvailable = activeAvailable
				this.state.currentWatchDiscontinued = slideElement.dataset.watchDiscontinued === 'true'
				this.state.currentWatchUrl = activeUrl
				this.state.currentWatchName = activeName
			} else {
				activeName = slideElement.dataset.productTitle || ''
				activePriceCents = parseInt(slideElement.dataset.priceCents, 10) || 0
				activeAvailable = slideElement.dataset.available !== 'false'
				activeUrl = slideElement.dataset.productUrl || ''

				this.state.currentStrapVariantId = slideElement.dataset.variantId || ''
				this.state.currentStrapId = slideElement.dataset.productId || ''
				this.state.currentStrapPriceCents = activePriceCents
				this.state.currentStrapAvailable = activeAvailable
				this.state.currentStrapUrl = activeUrl
				this.state.currentStrapName = activeName
			}

			const nameElement = strapInfo.querySelector('.strap-name')
			const priceElement = this.querySelector('.strap-price')

			if (nameElement) {
				const suffix = activeAvailable ? '' : this.state.currentWatchDiscontinued ? ' — Sold Out' : ' — Out of stock'
				if (this.mode === 'watches' && activeUrl) {
					const a = document.createElement('a')
					a.href = activeUrl
					a.textContent = activeName
					nameElement.replaceChildren(a, document.createTextNode(suffix))
				} else {
					nameElement.textContent = `${activeName}${suffix}`
				}
			}
			if (priceElement) {
				priceElement.textContent = this.formatMoney(activePriceCents, this.currency)
			}

			strapInfo.classList.toggle('is-sold-out', !activeAvailable)

			if (this.notifyLink) {
				let notifyUrl = null
				if (this.mode === 'watches') {
					if (!this.state.currentStrapAvailable && this.state.currentStrapUrl) {
						notifyUrl = this.state.currentStrapUrl
					} else if (!activeAvailable && activeUrl) {
						notifyUrl = activeUrl
					}
				} else if (!activeAvailable && activeUrl) {
					notifyUrl = activeUrl
				}
				if (notifyUrl && !this.state.currentWatchDiscontinued) {
					this.notifyLink.href = notifyUrl
					this.notifyLink.hidden = false
				} else {
					this.notifyLink.hidden = true
				}
			}

			this.updatePurchaseForms()
		}

		onCartFormSubmit(event) {
			event.preventDefault()

			const form = event.currentTarget
			if (!form || form.dataset.submitting === 'true') return
			const button = form.querySelector('button[type="submit"], button')

			form.dataset.submitting = 'true'
			if (button) button.setAttribute('disabled', '')

			const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
				method: 'POST',
				headers: { 'Accept': 'application/javascript' }
			}
			config.headers['X-Requested-With'] = 'XMLHttpRequest'
			delete config.headers['Content-Type']

			const formData = new FormData(form)

			if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
				formData.append(
					'sections',
					this.cart.getSectionsToRender().map(section => section.section)
				)
				formData.append('sections_url', window.location.pathname)
				if (typeof this.cart.setActiveElement === 'function') {
					this.cart.setActiveElement(document.activeElement)
				}
			}

			config.body = formData

			const cartAddUrl = (typeof routes !== 'undefined' && routes.cart_add_url) ? routes.cart_add_url : '/cart/add'

			fetch(cartAddUrl, config)
				.then(response => response.json())
				.then(response => {
					if (response.status || response.errors) {
						const msg = response.description || response.message || (response.errors && JSON.stringify(response.errors)) || 'Could not add to cart.'
						console.error('[watch-configurator-v2-auto] cart/add error', response)
						alert(msg)
						return
					}

					if (this.cart && typeof this.cart.renderContents === 'function') {
						this.cart.renderContents(response)
					} else if (typeof updateCartCounters === 'function') {
						updateCartCounters()
					}

					if (typeof updateFreeShipping === 'function') {
						try { updateFreeShipping() } catch (e) {}
					}
				})
				.catch(err => {
					console.error('[watch-configurator-v2-auto] cart/add network error', err)
					alert('Unable to add item to cart. Please try again.')
				})
				.finally(() => {
					form.dataset.submitting = 'false'
					if (button) button.removeAttribute('disabled')
				})
		}

		updatePurchaseForms() {
			const strapAvailable = this.state.currentStrapAvailable
			const watchAvailable = this.state.currentWatchAvailable

			// Per-product hide-logic (mode-agnostic):
			//   - watch-only-form depends on watch availability only
			//   - strap-only-form depends on strap availability only
			//   - bundle-form requires BOTH available
			const watchVariantOk = !!this.state.currentWatchVariantId
			const strapVariantOk = !!this.state.currentStrapVariantId
			if (this.watchOnlyForm) this.watchOnlyForm.hidden = !watchAvailable || !watchVariantOk
			if (this.strapOnlyForm) this.strapOnlyForm.hidden = !strapAvailable || !strapVariantOk
			if (this.bundleForm) this.bundleForm.hidden = !watchAvailable || !strapAvailable || !watchVariantOk || !strapVariantOk

			this.updateForm(this.watchOnlyForm, 'id', this.state.currentWatchVariantId, !this.state.currentWatchVariantId || !watchAvailable)

			const strapBuyable = this.state.currentStrapVariantId && strapAvailable
			this.updateForm(this.strapOnlyForm, 'id', this.state.currentStrapVariantId, !strapBuyable)

			if (this.bundleForm) {
				const watchInput = this.bundleForm.querySelector('input[name="items[0][id]"]')
				const strapInput = this.bundleForm.querySelector('input[name="items[1][id]"]')
				const button = this.bundleForm.querySelector('button')

				if (watchInput) watchInput.value = this.state.currentWatchVariantId
				if (strapInput) strapInput.value = this.state.currentStrapVariantId

				const bundleBuyable = this.state.currentWatchVariantId && this.state.currentStrapVariantId && this.state.currentStrapAvailable && this.state.currentWatchAvailable
				if (button) {
					button.disabled = !bundleBuyable
					const baseText = button.dataset.baseText || ''
					if (bundleBuyable && baseText) {
						const total = this.state.currentWatchPriceCents + this.state.currentStrapPriceCents
						button.textContent = `${baseText} — ${this.formatMoney(total, this.currency)}`
					} else if (baseText) {
						button.textContent = baseText
					}
				}
			}
		}

		updateForm(form, inputName, value, disabled) {
			if (!form) return

			const input = form.querySelector(`input[name="${inputName}"]`)
			const button = form.querySelector('button')

			if (input) input.value = value || ''
			if (button) button.disabled = disabled
		}

		disconnectedCallback() {
			this.cancelManagedAsync()
			this.destroySwiper()
			window.removeEventListener('resize', this._onResize)
			document.removeEventListener('shopify:section:load', this._onSectionLoad)
			document.removeEventListener('DOMContentLoaded', this._onDOMReady)
			if (this.styleTablist) {
				this.styleTablist.removeEventListener('keydown', this._onStyleTablistKey)
			}
			if (this.faceTablist) {
				this.faceTablist.removeEventListener('keydown', this._onFaceTablistKey)
			}
			if (this.observer) {
				this.observer.disconnect()
			}
			if (this._hydrationObserver) {
				this._hydrationObserver.disconnect()
				this._hydrationObserver = null
			}
		}
	}

	customElements.define('watch-strap-configurator-v2', WatchStrapConfiguratorV2)
}
